import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { buildClubHref, resolveClubReference } from '@/features/catalog/catalog.api';
import { fetchMyClubs } from '@/features/my-clubs/my-clubs.api';
import { trackEvent } from '@/shared/lib/analytics';
import { trackYandexGoal } from '@/shared/lib/yandex-metrica';
import { PageFrame } from '@/shared/components/ui/PageFrame';
import { SectionHeader } from '@/shared/components/ui/SectionHeader';
import { MiniappSurface } from '@/shared/components/ui/MiniappSurface';
import { PrimaryButton } from '@/shared/components/ui/PrimaryButton';
import { NeutralLoadingState } from '@/shared/components/ui/NeutralLoadingState';
import { ContinueCard } from '@/shared/components/club/ContinueCard';
import { ClubCard } from '@/shared/components/club/ClubCard';

function formatClubCount(count: number): string {
  const mod10 = count % 10;
  const mod100 = count % 100;

  if (mod10 === 1 && mod100 !== 11) return `${count} активный клуб`;
  if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) return `${count} активных клуба`;
  return `${count} активных клубов`;
}

function MyClubsTop({ count }: { count?: number }) {
  return (
    <div className="space-y-1">
      <h1 className="text-[28px] font-black uppercase leading-[0.9] tracking-[0.01em] text-[var(--foreground)] min-[430px]:text-[32px] min-[430px]:tracking-[0.02em]">
        МОИ КЛУБЫ
      </h1>
      {typeof count === 'number' ? (
        <p className="text-[13px] font-medium text-[rgba(26,26,26,0.42)] min-[430px]:text-[14px]">
          {formatClubCount(count)}
        </p>
      ) : null}
    </div>
  );
}

export function MyClubsPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['my-clubs'],
    queryFn: fetchMyClubs,
    staleTime: 0,
  });

  useEffect(() => {
    trackEvent('my_clubs_view');
  }, []);

  useEffect(() => {
    if (!data?.items.length) {
      return;
    }

    for (const item of data.items) {
      if (item.progressPercent < 80) {
        continue;
      }

      trackYandexGoal(
        'club_read_80',
        {
          club_id: item.clubId,
          source_surface: 'my_clubs',
          progress_percent: item.progressPercent,
          milestone: 'read_80',
        },
        {
          dedupeKey: `${item.clubId}:read_80`,
        },
      );
    }
  }, [data]);

  if (isLoading) {
    return (
      <PageFrame className="space-y-4">
        <MyClubsTop />
        <NeutralLoadingState rows={4} ariaLabel="Загрузка клубов" />
      </PageFrame>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <PageFrame className="space-y-4">
        <MyClubsTop />
        <MiniappSurface className="space-y-4 px-5 py-6 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[rgba(107,78,230,0.12)] text-2xl">
            📚
          </div>
          <div className="space-y-2">
            <h2 className="text-[18px] font-black uppercase tracking-[0.02em] text-[var(--foreground)]">
              Пока пусто
            </h2>
            <p className="text-[14px] leading-6 text-[rgba(26,26,26,0.58)]">
              Найдите интересный клуб в каталоге и вернитесь сюда, чтобы продолжить чтение.
            </p>
          </div>
          <PrimaryButton
            onClick={() => {
              trackEvent('my_clubs_empty_cta_click', { target: 'catalog' });
              void navigate('/catalog');
            }}
            className="w-full"
          >
            Перейти в каталог
          </PrimaryButton>
        </MiniappSurface>
      </PageFrame>
    );
  }

  const { continueItem, items } = data;
  const listItems = continueItem ? items.filter((club) => club.clubId !== continueItem.clubId) : items;
  const continueAuthor = continueItem
    ? items.find((club) => club.clubId === continueItem.clubId)?.author ?? null
    : null;

  return (
    <PageFrame className="space-y-4">
      <MyClubsTop count={data.items.length} />

      {continueItem && (
        <ContinueCard
          item={continueItem}
          clubAuthor={continueAuthor}
          onContinue={() => {
            trackEvent('continue_click', {
              clubId: continueItem.clubId,
              chapterId: continueItem.chapterId,
            });
            void navigate(
              `/reader/${resolveClubReference(continueItem) ?? continueItem.clubId}/${continueItem.chapterId}?source=my-clubs`,
            );
          }}
        />
      )}

      {listItems.length > 0 && (
        <div className="space-y-2">
          <SectionHeader label="ВСЕ КЛУБЫ" />
          <MiniappSurface className="overflow-hidden p-0">
            {listItems.map((item, index) => (
              <div
                key={item.clubId}
                className={index === 0 ? '' : 'border-t border-[rgba(26,26,26,0.06)]'}
              >
                <ClubCard
                  club={item}
                  onClick={() => {
                    trackEvent('my_clubs_club_open', { clubId: item.clubId });
                    void navigate(buildClubHref(item, 'my-clubs'));
                  }}
                />
              </div>
            ))}
          </MiniappSurface>
        </div>
      )}
    </PageFrame>
  );
}
